/* eslint-disable react/no-unstable-nested-components */
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import {
    Badge,
    Button,
    Dialog,
    DialogContent,
    Table,
    Tooltip,
} from "@jhool-io/fe-components";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "../../../../../hooks/useRedux";
import { useFetchProvidersCaseload } from "../../../../providers/hooks/providers.queries";
import { useDebounce } from "../../../../../hooks/helpers";
import Skeleton from "../../../../../components/Skeleton/Skeleton";
import ListState from "../../../../../components/ListState/ListState";
import { IProviderCaseloadDashboard } from "../../../../providers/types/providers.types";
import {
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    formatDate,
    handleDisplayClientEmailCorrectly,
    makeStringFirstLetterCapital,
} from "../../../../../utils/helpers";
import { isMinor } from "../../../../../utils/helpers/age";
import MultiplyCircleIcon from "../../../../../components/Icons/MultiplyCircle";
import CancelSessionForm from "../../../../schedule/components/ClinicianSchedule/CaseloadView/CancelSessionForm/CancelSessionForm";
import RecordIcon from "../../../../../components/Icons/Record";
import UserOctagonIcon from "../../../../../components/Icons/UserOctagon";
import { usePauseProviderClient } from "../../../../providers/hooks/providers.mutations";
import useToast from "../../../../../hooks/useToast";
import PauseIcon from "../../../../../components/Icons/Pause";
import ProfileDeleteIcon from "../../../../../components/Icons/ProfileDelete";
import useNewBackground from "../../../../../hooks/useNewBackground";

type Dialogs =
    | "cancel_session"
    | "pause_client"
    | "unpause_client"
    | "terminate_client";

export default function CaseloadList() {
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [appointmentInView, setAppointmentInView] =
        useState<IProviderCaseloadDashboard | null>(null);
    const [shouldNavigate, setShouldNavigate] = useState(false);

    const [searchParams] = useSearchParams();

    const navigate = useNavigate();

    const { practice } = useAppSelector((state) => state.userPractice);

    const useNewBg = useNewBackground();

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const searchFilter = searchParams.get("search") || "";
    const filter = searchParams.get("filter");
    const showPausedFilter = searchParams.get("show_paused");

    const debouncedSearchFilter = useDebounce(searchFilter);

    const pauseClient = usePauseProviderClient();

    const isCancelSessionMutating = useIsMutating(["cancel-session"]);

    const { data, isLoading, isSuccess, error } = useFetchProvidersCaseload({
        providerId: practice?.provider_id || "",
        search_string: debouncedSearchFilter,
        enabled: Boolean(practice?.provider_id),
        show_paused_client: showPausedFilter || "",
        show_clients_with_pending_tasks:
            filter === "clients_with_pending_task" ? "true" : "",
    });

    const handleRowClick = (row: Row<IProviderCaseloadDashboard>) => {
        navigate(`/clients/${row.original.client_id}?tab=session_notes`);
    };

    const handleDialogInView = useCallback(
        (
            dialog: Dialogs | null,
            appointment: IProviderCaseloadDashboard | null
        ) => {
            setDialogInView(dialog);
            setAppointmentInView(appointment);
        },
        []
    );

    const formattedData: IProviderCaseloadDashboard[] = [];

    data?.data?.forEach((item) => {
        const { client } = item;
        item.appointments.forEach((innerItem) => {
            formattedData.push({
                ...client,
                ...innerItem,
            });
        });
    });

    const handleRowStyle = () => {
        return {
            backgroundColor: "#FEF5E9",
            borderBottom: "1px solid #ECEEF2",
            color: "#0B132B",
        };
    };

    const handlePauseClient = (payload: "pause" | "unpause") => {
        pauseClient.mutate(
            {
                client_ids: [appointmentInView?.client_id || ""],
                action_type: payload,
            },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message: res.message || "Client paused successfully",
                    });
                    handleDialogInView(null, null);
                    queryClient.invalidateQueries({
                        queryKey: ["caseload"],
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not pause client at this time",
                    });
                },
            }
        );
    };

    const columns: ColumnDef<IProviderCaseloadDashboard>[] = useMemo(
        () => [
            {
                accessorKey: "client",
                header: "",
                cell: ({ row }) => (
                    <div className="relative">
                        <span>
                            {row.original.therapy_location === "remote" ? (
                                <RecordIcon />
                            ) : (
                                <UserOctagonIcon />
                            )}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "session_history_id",
                header: "Actions",
                cell: ({ row }) => (
                    <div
                        className="px-[6px] h-[28px] inline-flex items-center gap-x-[18px] rounded-r6 bg-white
                        }  border border-[rgba(231,231,231,0.91)] shadow-[0px_6px_10px_0px_rgba(14,18,27,0.06),_0px_2px_4px_0px_rgba(14,18,27,0.03)] self-start"
                    >
                        <Tooltip
                            content={
                                row.original.is_paused
                                    ? "Unpause client session"
                                    : "Pause client session"
                            }
                        >
                            <Button
                                size="auto"
                                variant="normal"
                                className={`p-4 ${
                                    useNewBg
                                        ? "hover:bg-[#FCEFDD]"
                                        : "hover:bg-[var(--color-gray-200)]"
                                }`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDialogInView(
                                        row.original.is_paused
                                            ? "unpause_client"
                                            : "pause_client",
                                        row.original
                                    );
                                }}
                            >
                                <PauseIcon />
                            </Button>
                        </Tooltip>
                        <Tooltip content="Cancel session">
                            <Button
                                size="auto"
                                variant="normal"
                                className={`p-4 ${
                                    useNewBg
                                        ? "hover:bg-[#FCEFDD]"
                                        : "hover:bg-[var(--color-gray-200)]"
                                }`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDialogInView(
                                        "cancel_session",
                                        row.original
                                    );
                                }}
                            >
                                <MultiplyCircleIcon />
                            </Button>
                        </Tooltip>
                        <Tooltip content="Terminate client">
                            <Button
                                className={`p-4 ${
                                    useNewBg
                                        ? "hover:bg-[#FCEFDD]"
                                        : "hover:bg-[var(--color-gray-200)]"
                                }`}
                                variant="normal"
                                size="auto"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDialogInView(
                                        "terminate_client",
                                        row.original
                                    );
                                }}
                            >
                                <ProfileDeleteIcon fill="#5A5F6A" />
                            </Button>
                        </Tooltip>
                    </div>
                ),
            },
            {
                accessorKey: "client",
                header: "CLIENT NAME",
                cell: ({ row }) => (
                    <span className="flex items-center gap-x-4">
                        {displayNameInRightFormat({
                            firstName: row.original.first_name,
                            lastName: row.original.last_name,
                        })}
                        {isMinor(row.original.age) ? (
                            <span>
                                <Badge
                                    content="Minor"
                                    textColor="#981F41"
                                    bgColor="rgba(251, 199, 198, 0.50)"
                                />
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                accessorKey: "preferred_name",
                header: "PREFERRED NAME",
                cell: ({ row }) => row.original.preferred_name || "--",
            },
            {
                accessorKey: "pronouns",
                header: "PRONOUNS",
                cell: ({ row }) => row.original.pronouns || "--",
            },
            {
                accessorKey: "email",
                header: "EMAIL",
                cell: ({ row }) =>
                    handleDisplayClientEmailCorrectly(row.original.email),
            },
            {
                accessorKey: "contact",
                header: "Phone no",
                cell: ({ row }) => row.original.contact || "--",
            },
            {
                accessorKey: "recurrence_rule",
                header: "APPOINTMENT",
                cell: ({ row }) => (
                    <Badge
                        bgColor="#FBDFBA"
                        content={`${
                            row.original.session_day
                        }, ${convertTimeTo12HoursFormat(
                            `${row.original.session_start_hr}:${row.original.session_start_min}`
                        )} ${makeStringFirstLetterCapital(
                            row.original.recurrence_rule
                        )}`}
                        textColor="#6D440E"
                    />
                ),
            },
            {
                accessorKey: "client_id",
                header: "starting on",
                cell: ({ row }) =>
                    row.original.first_session_date
                        ? formatDate(row.original.first_session_date)
                        : "--",
            },
        ],
        [handleDialogInView, useNewBg]
    );

    return (
        <>
            {appointmentInView?.client_id ? (
                <Dialog open={dialogInView === "cancel_session"}>
                    <DialogContent
                        title="Cancel session"
                        variant="center"
                        onSaveClick={() => setShouldNavigate(true)}
                        handleCloseDialog={() => handleDialogInView(null, null)}
                        isCancelBtnDisabled={isCancelSessionMutating > 0}
                        isSubmitBtnDisabled={isCancelSessionMutating > 0}
                        onCancelClick={() => setShouldNavigate(false)}
                        cancelText="Yes,cancel"
                        saveText="No,create No show note"
                        canCancelButtonSubmit
                        cancelBtnFormValue="cancel_client_session"
                        submitBtnFormValue="cancel_client_session"
                    >
                        <CancelSessionForm
                            onFormSubmit={() => handleDialogInView(null, null)}
                            shouldNavigate={shouldNavigate}
                            sessionHistoryId={
                                appointmentInView?.session_history_id || null
                            }
                            action="cancel"
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <Dialog open={dialogInView === "terminate_client"}>
                <DialogContent
                    title="Terminate Client"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => handleDialogInView(null, null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    cancelText="Cancel"
                    saveText="Terminate & Write Note"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => handleDialogInView(null, null)}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={
                            appointmentInView?.session_history_id || null
                        }
                        externalSessionId={
                            appointmentInView?.external_session_id || ""
                        }
                        intakeClientId={
                            appointmentInView?.intake_client_id || null
                        }
                        action="terminate"
                    />
                </DialogContent>
            </Dialog>
            {appointmentInView?.client_id ? (
                <Dialog open={dialogInView === "pause_client"}>
                    <DialogContent
                        title="Pause client"
                        variant="center"
                        onSaveClick={() => handlePauseClient("pause")}
                        handleCloseDialog={() => setDialogInView(null)}
                        isCancelBtnDisabled={pauseClient.isLoading}
                        isSubmitBtnDisabled={pauseClient.isLoading}
                        classNames="pt-24 pb-48"
                        saveText="Pause client"
                    >
                        <div>
                            <h3 className="font-medium text-xl mb-8">
                                Are you sure you want to pause client
                            </h3>
                            <p className="text-gray font-normal text-base">
                                By pausing this client, your appointment with
                                the client will be paused.
                            </p>
                        </div>
                    </DialogContent>
                </Dialog>
            ) : null}
            {appointmentInView?.client_id ? (
                <Dialog open={dialogInView === "unpause_client"}>
                    <DialogContent
                        title="Unpause client"
                        variant="center"
                        onSaveClick={() => handlePauseClient("unpause")}
                        handleCloseDialog={() => setDialogInView(null)}
                        isCancelBtnDisabled={pauseClient.isLoading}
                        isSubmitBtnDisabled={pauseClient.isLoading}
                        classNames="pt-24 pb-48"
                        saveText="Unpause client"
                    >
                        <div>
                            <h3 className="font-medium text-xl mb-8">
                                Are you sure you want to Unpause client
                            </h3>
                            <p className="text-gray font-normal text-base">
                                By Unpausing this client, your appointment with
                                the client will be Unpaused.
                            </p>
                        </div>
                    </DialogContent>
                </Dialog>
            ) : null}
            <div>
                {isLoading ? (
                    <Skeleton type="table" containerTestId="loader" />
                ) : null}
                {error ? (
                    <div className="h-full text-sm font-medium flex items-center justify-center">
                        <ListState
                            isError
                            errorMsg={
                                error?.response?.data?.message ||
                                "Could not load caseload at this time"
                            }
                            stateHelperText="Try reloading this page to solve this issue"
                            context="general"
                            cardClass="h-[350px]"
                            mode={useNewBg ? "new" : "old"}
                        />
                    </div>
                ) : null}
                {isSuccess && data.data.length === 0 ? (
                    <ListState
                        stateHelperText="No caseload to display"
                        isError={false}
                        emptyMessage="No caseload"
                        context="general"
                        cardClass="h-[350px]"
                        mode={useNewBg ? "new" : "old"}
                    />
                ) : null}
                {isSuccess && data.data.length > 0 ? (
                    <div>
                        <Table
                            columns={columns}
                            data={formattedData}
                            handleRowClick={handleRowClick}
                            handleRowStyle={handleRowStyle}
                            className="mb-[15px]"
                        />
                    </div>
                ) : null}
            </div>
        </>
    );
}
